import axios from 'axios';
import { config } from '@/config';
import _ from 'lodash';

const clientApiInstance = axios.create({
    baseURL: config.api.url,
    timeout: config.api.timeout,
    headers: {
        Authorization: "Bearer " + window.RETURNS_CENTRE_GLOBALS.token
    },
});

const setAuthTokenForApiInstance = (token: string) => {
    clientApiInstance.defaults.headers.Authorization = "Bearer " + token;
};

clientApiInstance.interceptors.response.use(
    (response) => {
        if (response.headers['x-authorization-token']) {
            setAuthTokenForApiInstance(response.headers['x-authorization-token']);
        }
        return response;
    },
    (error) => {
        if (error.response.headers['x-authorization-token']) {
            setAuthTokenForApiInstance(error.response.headers['x-authorization-token']);
        }
        return Promise.reject(error);
    }
);

const shopDomain = window.RETURNS_CENTRE_GLOBALS.shop_domain;

class ClientApi {
    readonly domain: string = config.shopDomain;
    async getCountries(): Promise<CountryList> {
        const res = await clientApiInstance.get<CountryListResponse>('/client/countries', {
            params: {
                shop_domain: shopDomain
            }
        });
        return res.data.data;
    }

    async getShop(shopDomain: string): Promise<Shop> {
        const res = await clientApiInstance.get<ShopResponse>('/client/shop', {
            params: {
                shop_domain: shopDomain
            }
        });
        return res.data.data;
    }

    async verifyOrder(data: OrderToVerify): Promise<VerifiedOrderResponse> {
        const res = await clientApiInstance.post<VerifiedOrderResponse>('/client/verify-order', {
            ...data
        } as VerifyOrderRequest);

        return res.data;
    }

    async getCustomerOrders(customer: (ReturnsPortalShopifyCustomer|Customer))  {
        const key: string = (_.hasIn(customer, 'hmac')) ? 'shopify_customer_id' : 'id';
        const res = await clientApiInstance.get('/client/trade-in/customer/orders', {
            params: {
                [key]: customer.id,
                shop_domain: shopDomain
            }
        });
        return res.data;
    }

    async getCustomerTradeIns<T extends object>(params: T): Promise<TradeInsIndexResponse> {
        const res = await clientApiInstance.get<TradeInsIndexResponse>('/client/trade-ins', {
            params: { ...params, shop_domain: shopDomain }
        });
        return res.data;
    }
    async getCategories(): Promise<Categories> {
        const res = await clientApiInstance.get<CategoriesResponse>('/client/trade-in/categories', {
            params: {shop_domain:shopDomain}
        });
        return res.data;
    }
    // async getProductOptions(category:Category): Promise<ProductOption[]> {
    //     const res = await clientApiInstance.get<ProductOptionsResponse>('/client/product-options', {
    //         params: {
    //             category_id:category.id,
    //             shop_domain:shopDomain}
    //     });
    //     return res.data.data;
    // }

    async confirmTradeIn(id: number, data: ConfirmTradeIn): Promise<ConfirmTradeInResponse|ConfirmTradeInExchangeResponse>{
        const res = await clientApiInstance.put<ConfirmTradeInResponse|ConfirmTradeInExchangeResponse>('/client/trade-ins/' + id , {
            ...data,
            shop_domain: shopDomain
        } as ConfirmTradeInRequest);
        return res.data;
    }

    async createTradeIn(data: CreateTradeIn): Promise<TradeIn>  {
        const res = await clientApiInstance.post<{ data: TradeIn }>('/client/trade-ins', {
            ...data,
            shop_domain: shopDomain
        } as TradeInCreateRequest);
        return res.data.data;
    }

    async getReturnCarrierServices(tradeInId: number): Promise<ReturnCarrierService[]>  {
        const res = await clientApiInstance.get<ReturnCarrierServiceResponse>(
            '/client/return-carrier-services' , {
                params: {
                    trade_in_id: tradeInId,
                    shop_domain: shopDomain
                }
            });
        return res.data.data;
    }

    async getTradeIn(id: number, params: object) {
        const res = await clientApiInstance.get('/client/trade-ins/' + id, {
            params: {
                ...params,
                shop_domain: shopDomain
            }
        });
        return res.data.data;
    }
    async getTranslations()  {
        const res = await clientApiInstance.get('/client/return-centre-translations', {
            params: {
                shop_domain: shopDomain
            }
        });
        return res.data.data;
    }
}

export const clientApi = new ClientApi();
